import { Component, inject } from '@angular/core'
import { RouterOutlet, NavigationEnd, Router } from '@angular/router'
import { Title, Meta } from '@angular/platform-browser'
import { TranslateService } from '@ngx-translate/core'
import { HeaderComponent } from './core/header/header.component'
import { FooterComponent } from './core/footer/footer.component'
import { PreferencesService } from './services/preferences.service'
import { filter } from 'rxjs/operators'

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private translate = inject(TranslateService)
  private preferences = inject(PreferencesService)
  private titleService = inject(Title)
  private metaService = inject(Meta)
  private router = inject(Router)

  constructor() {
    // Load user preferences
    const savedLanguage = this.preferences.getLanguage()

    // Initialize translation service with saved language
    this.translate.setDefaultLang('fr')
    this.translate.use(savedLanguage)
    console.log(`✅ Translation service initialized with ${savedLanguage}`)
    console.log(`💾 User preferences loaded from localStorage`)

    // Update page title and meta tags when language changes
    this.translate.onLangChange.subscribe(() => {
      this.updateMetaTags()
    })

    // Update page title and meta tags on route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateMetaTags()
      })

    // Initial meta tags update
    this.updateMetaTags()
  }

  private updateMetaTags(): void {
    // Update page title
    this.translate.get('seo.title').subscribe((title: string) => {
      this.titleService.setTitle(title)
    })

    // Update meta description
    this.translate.get('seo.description').subscribe((description: string) => {
      this.metaService.updateTag({ name: 'description', content: description })
      this.metaService.updateTag({
        property: 'og:description',
        content: description,
      })
      this.metaService.updateTag({
        name: 'twitter:description',
        content: description,
      })
    })

    // Update meta keywords
    this.translate.get('seo.keywords').subscribe((keywords: string) => {
      this.metaService.updateTag({ name: 'keywords', content: keywords })
    })

    // Update Open Graph title
    this.translate.get('seo.og_title').subscribe((ogTitle: string) => {
      this.metaService.updateTag({ property: 'og:title', content: ogTitle })
      this.metaService.updateTag({ name: 'twitter:title', content: ogTitle })
    })

    // Update language
    const currentLang = this.translate.currentLang || 'fr'
    document.documentElement.lang = currentLang
    this.metaService.updateTag({
      name: 'language',
      content: currentLang === 'fr' ? 'French' : 'English',
    })
    this.metaService.updateTag({
      property: 'og:locale',
      content: currentLang === 'fr' ? 'fr_FR' : 'en_US',
    })
  }
}
